import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { Bell, Mail, MessageSquare, Loader2, RefreshCw, Filter } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Pagination, type PageMeta } from '@/components/Pagination';
import { apiCall } from '@/lib/api';

type Channel = 'email' | 'sms';
type Status = 'sent' | 'skipped' | 'failed';

interface NotifRow {
  id: string;
  channel: Channel;
  template: string;
  recipient: string;
  status: Status;
  member_id: string | null;
  member_name: string | null;
  context_json: string;
  sent_at: string;
}

interface Resp extends PageMeta {
  items: NotifRow[];
  summary: Array<{ status: string; n: number }>;
}

export default function Notifications() {
  const { getToken } = useAuth();
  const [data, setData] = useState<Resp | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [filterChannel, setFilterChannel] = useState<'' | Channel>('');
  const [filterStatus, setFilterStatus] = useState<'' | Status>('');

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const params = new URLSearchParams();
      if (filterChannel) params.set('channel', filterChannel);
      if (filterStatus) params.set('status', filterStatus);
      params.set('page', String(page));
      params.set('per_page', '50');
      const token = await getToken();
      const r = await apiCall<Resp>(`/v1/notifications?${params}`, { bearerToken: token });
      setData(r);
    } catch (e) { setErr(e instanceof Error ? e.message : 'Failed'); }
    finally { setLoading(false); }
  }, [getToken, filterChannel, filterStatus, page]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { setPage(1); }, [filterChannel, filterStatus]);

  const summary = Object.fromEntries((data?.summary ?? []).map((s) => [s.status, s.n] as const));
  const skippedAny = (summary.skipped ?? 0) > 0;

  return (
    <div className="space-y-4 max-w-5xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Notifications</h1>
          <p className="text-sm text-muted-foreground">
            Every email and SMS the system would send — including ones skipped because keys aren't set.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />} Refresh
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid gap-3 grid-cols-3">
        <SummaryCard label="Sent"    value={summary.sent ?? 0}    tone="ok" />
        <SummaryCard label="Skipped" value={summary.skipped ?? 0} tone="warn" />
        <SummaryCard label="Failed"  value={summary.failed ?? 0}  tone={summary.failed ? 'err' : 'ok'} />
      </div>

      {skippedAny && (
        <Card className="border-amber-200 dark:border-amber-900/50">
          <CardContent className="py-3 text-xs text-amber-900 dark:text-amber-200 flex gap-2">
            <Bell className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              Skipped notifications mean SendGrid (email) or Twilio (SMS) credentials aren't configured on the API.
              Set <code className="font-mono">SENDGRID_API_KEY</code> + <code className="font-mono">FROM_EMAIL</code> for email,
              or <code className="font-mono">TWILIO_*</code> for SMS, then restart the API to start sending.
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="py-3 flex items-center gap-3 flex-wrap text-sm">
          <span className="flex items-center gap-1 text-muted-foreground"><Filter className="h-4 w-4" /> Filter</span>
          <select value={filterChannel} onChange={(e) => setFilterChannel(e.target.value as Channel | '')} className="rounded border bg-background px-2 py-1 text-sm">
            <option value="">All channels</option>
            <option value="email">Email</option>
            <option value="sms">SMS</option>
          </select>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value as Status | '')} className="rounded border bg-background px-2 py-1 text-sm">
            <option value="">All statuses</option>
            <option value="sent">Sent</option>
            <option value="skipped">Skipped</option>
            <option value="failed">Failed</option>
          </select>
        </CardContent>
      </Card>

      {err && <Card><CardContent className="py-4 text-sm text-destructive">{err}</CardContent></Card>}

      {data && data.items.length === 0 && !loading && (
        <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">
          <Bell className="mx-auto mb-3 h-8 w-8 opacity-40" />
          No notifications match your filters.
        </CardContent></Card>
      )}

      {data && data.items.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground border-b">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">When</th>
                  <th className="text-left px-4 py-2 font-medium">Channel</th>
                  <th className="text-left px-4 py-2 font-medium">Template</th>
                  <th className="text-left px-4 py-2 font-medium">Recipient</th>
                  <th className="text-left px-4 py-2 font-medium">Member</th>
                  <th className="text-left px-4 py-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((n) => (
                  <tr key={n.id} className="border-b last:border-0 hover:bg-muted/40">
                    <td className="px-4 py-2 text-xs text-muted-foreground tabular-nums">{new Date(n.sent_at).toLocaleString()}</td>
                    <td className="px-4 py-2">
                      <Badge variant="outline" className="gap-1">
                        {n.channel === 'email' ? <Mail className="h-3 w-3" /> : <MessageSquare className="h-3 w-3" />}
                        {n.channel}
                      </Badge>
                    </td>
                    <td className="px-4 py-2 font-mono text-xs">{n.template}</td>
                    <td className="px-4 py-2 text-xs">{n.recipient}</td>
                    <td className="px-4 py-2 text-muted-foreground">{n.member_name ?? '—'}</td>
                    <td className="px-4 py-2">
                      <Badge variant={n.status === 'sent' ? 'default' : n.status === 'failed' ? 'destructive' : 'outline'}>
                        {n.status}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <Pagination meta={data} onPageChange={setPage} itemLabel="notifications" />
    </div>
  );
}

function SummaryCard({ label, value, tone }: { label: string; value: number; tone: 'ok' | 'warn' | 'err' }) {
  const colors = { ok: '', warn: 'text-amber-600', err: 'text-destructive' } as const;
  return (
    <Card>
      <CardContent className="py-4">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className={'text-2xl font-semibold mt-1 tabular-nums ' + colors[tone]}>{value}</div>
      </CardContent>
    </Card>
  );
}
